import {
  useComentarios,
  useEliminarComentario,
  useGuardarComentario,
} from '@/hooks/useComentarios';
import type { ComentarioScope } from '@adelante/shared';
import { MessageSquarePlus, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';

interface Props {
  anio: number;
  mes: number;
  scope: ComentarioScope;
  seccionId?: string | null;
}

export function ComentarioSeccion({ anio, mes, scope, seccionId = null }: Props) {
  const { data: comentarios } = useComentarios(anio, mes);
  const guardar = useGuardarComentario();
  const eliminar = useEliminarComentario();

  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');

  const comentario =
    comentarios?.find((c) => c.scope === scope && (c.seccion_id ?? null) === (seccionId ?? null)) ??
    null;

  useEffect(() => {
    if (editando) setTexto(comentario?.contenido_markdown ?? '');
  }, [editando, comentario?.contenido_markdown]);

  function handleGuardar() {
    guardar.mutate(
      { anio, mes, scope, seccion_id: seccionId ?? null, contenido_markdown: texto.trim() },
      { onSuccess: () => setEditando(false) },
    );
  }

  function handleEliminar() {
    if (!comentario) return;
    eliminar.mutate({ id: comentario.id_comentario, anio, mes });
  }

  if (editando) {
    return (
      <div
        data-pdf-ignore
        className="mt-4 space-y-2 rounded-md border border-border bg-muted/30 p-4"
      >
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escribe una nota sobre este período…"
          rows={4}
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleGuardar} disabled={!texto.trim() || guardar.isPending}>
            Guardar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditando(false)}
            disabled={guardar.isPending}
          >
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  if (comentario) {
    // NOTA: este bloque SÍ debe aparecer en el PDF — es contenido del usuario.
    // Solo ocultamos los botones de acción (editar/eliminar) con data-pdf-ignore.
    return (
      <div className="mt-4 rounded-md border border-border bg-muted/30 p-4">
        <div className="flex items-start justify-between gap-4">
          <p className="flex-1 whitespace-pre-wrap text-sm">{comentario.contenido_markdown}</p>
          <div data-pdf-ignore className="flex shrink-0 items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => setEditando(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-destructive hover:text-destructive"
              onClick={handleEliminar}
              disabled={eliminar.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {comentario.autor_nombre} ·{' '}
          {new Date(comentario.editado_en ?? comentario.creado_en).toLocaleString('es-CR')}
        </p>
      </div>
    );
  }

  return (
    <div data-pdf-ignore className="mt-4">
      <Button
        size="sm"
        variant="ghost"
        className="text-muted-foreground"
        onClick={() => setEditando(true)}
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        Agregar nota
      </Button>
    </div>
  );
}
